import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  ChevronLeft, 
  Check,
  Shield,
  AlertTriangle,
  CreditCard,
  Building,
  FileText,
  User,
  Mail,
  Phone,
  CheckCircle2,
  ArrowRight,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { COLORS, CITIES } from "./InvestOverviewPage";
import { INVESTMENT_LEVELS } from "./InvestmentDetailPage";

// ═══════════════════════════════════════════════════════════
// CHECKOUT PAGE
// ═══════════════════════════════════════════════════════════
export default function InvestmentCheckoutPage() {
  const { cityId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const levelId = searchParams.get('level') || 'growth';
  const level = INVESTMENT_LEVELS.find(l => l.id === levelId) || INVESTMENT_LEVELS[2];
  const city = CITIES.find(c => c.id === cityId) || CITIES[0];

  const [step, setStep] = useState(1); // 1: Review, 2: Personal, 3: Payment, 4: Success
  const [isProcessing, setIsProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState({
    risk: false,
    terms: false,
    privacy: false,
  });

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    postalCode: '',
    iban: '',
  });

  const allTermsAccepted = Object.values(acceptedTerms).every(Boolean);

  const handleSubmit = async () => {
    if (!allTermsAccepted) {
      toast.error("Bitte akzeptiere alle Bedingungen");
      return;
    }
    
    setIsProcessing(true);
    
    // Simulate processing
    await new Promise(resolve => setTimeout(resolve, 2500));
    
    setIsProcessing(false);
    setStep(4);
    toast.success("Investment erfolgreich!");
  };

  const renderStep = () => {
    switch(step) {
      case 1:
        return <ReviewStep />;
      case 2:
        return <PersonalStep />;
      case 3:
        return <PaymentStep />;
      case 4:
        return <SuccessStep />;
      default:
        return <ReviewStep />;
    }
  };

  // ═══════════════════════════════════════════════════════════
  // STEP 1: REVIEW
  // ═══════════════════════════════════════════════════════════
  const ReviewStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="px-5 py-6"
    >
      {/* Investment Summary Card */}
      <div 
        className="p-5 rounded-2xl mb-6"
        style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
      >
        <h3 className="text-sm font-semibold mb-4" style={{ color: COLORS.textMuted }}>
          DEIN INVESTMENT
        </h3>
        
        <div className="flex items-center gap-4 mb-4 pb-4" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl"
            style={{ backgroundColor: `${level.color}20` }}
          >
            {level.emoji}
          </div>
          <div className="flex-1">
            <p className="font-bold text-lg" style={{ color: COLORS.textPrimary }}>
              {level.name}
            </p>
            <p className="text-sm" style={{ color: COLORS.textMuted }}>
              {city.name}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold" style={{ color: level.color }}>
              {level.amount.toLocaleString('de-DE')}€
            </p>
          </div>
        </div>

        {/* Benefits */}
        <div className="space-y-2">
          {level.benefits.map((benefit, i) => (
            <div key={i} className="flex items-center gap-2">
              <Check className="w-4 h-4 flex-shrink-0" style={{ color: level.color }} />
              <span className="text-sm" style={{ color: COLORS.textSecondary }}>{benefit}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Profit Share Info */}
      <div 
        className="p-4 rounded-xl mb-6"
        style={{ backgroundColor: `${COLORS.primary}10`, border: `1px solid ${COLORS.primary}30` }}
      >
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: COLORS.primary }} />
          <div>
            <p className="font-semibold text-sm mb-1" style={{ color: COLORS.primary }}>
              Deine Gewinnbeteiligung
            </p>
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>
              Du erhältst deinen anteiligen Anteil an 20% des jährlichen Gewinns der Filiale {city.name}.
            </p>
          </div>
        </div>
      </div>

      {/* Risk Warning */}
      <div 
        className="p-4 rounded-xl mb-6"
        style={{ backgroundColor: `${COLORS.warning}10`, border: `1px solid ${COLORS.warning}30` }}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: COLORS.warning }} />
          <div>
            <p className="font-semibold text-sm mb-1" style={{ color: COLORS.warning }}>
              Risikohinweis
            </p>
            <p className="text-xs" style={{ color: COLORS.textSecondary }}>
              Dies ist eine unternehmerische Beteiligung. Es besteht die Möglichkeit eines 
              vollständigen Verlustes. Eine Rückerstattung ist nach Funding nicht vorgesehen.
            </p>
          </div>
        </div>
      </div>

      {/* Continue Button */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={() => setStep(2)}
        className="w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2"
        style={{ backgroundColor: COLORS.primary, color: COLORS.white }}
      >
        Weiter zu persönlichen Daten
        <ArrowRight className="w-5 h-5" />
      </motion.button>
    </motion.div>
  );

  // ═══════════════════════════════════════════════════════════
  // STEP 2: PERSONAL DATA
  // ═══════════════════════════════════════════════════════════
  const PersonalStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="px-5 py-6"
    >
      <h2 className="text-lg font-bold mb-1" style={{ color: COLORS.textPrimary }}>
        Persönliche Daten
      </h2>
      <p className="text-sm mb-6" style={{ color: COLORS.textMuted }}>
        Für die rechtsgültige Beteiligung benötigen wir folgende Angaben.
      </p>

      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="text-sm font-medium mb-2 block" style={{ color: COLORS.textSecondary }}>
            Vollständiger Name
          </label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: COLORS.textMuted }} />
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Max Mustermann"
              className="w-full pl-12 pr-4 py-3 rounded-xl text-sm"
              style={{ 
                backgroundColor: COLORS.bgCard,
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textPrimary,
              }}
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="text-sm font-medium mb-2 block" style={{ color: COLORS.textSecondary }}>
            E-Mail Adresse
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: COLORS.textMuted }} />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="max@email.de"
              className="w-full pl-12 pr-4 py-3 rounded-xl text-sm"
              style={{ 
                backgroundColor: COLORS.bgCard,
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textPrimary,
              }}
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label className="text-sm font-medium mb-2 block" style={{ color: COLORS.textSecondary }}>
            Telefonnummer
          </label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: COLORS.textMuted }} />
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="+49 123 456789"
              className="w-full pl-12 pr-4 py-3 rounded-xl text-sm"
              style={{ 
                backgroundColor: COLORS.bgCard,
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textPrimary,
              }}
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <label className="text-sm font-medium mb-2 block" style={{ color: COLORS.textSecondary }}>
            Adresse
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            placeholder="Musterstraße 123"
            className="w-full px-4 py-3 rounded-xl text-sm"
            style={{ 
              backgroundColor: COLORS.bgCard,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.textPrimary,
            }}
          />
        </div>

        {/* City & Postal */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium mb-2 block" style={{ color: COLORS.textSecondary }}>
              PLZ
            </label>
            <input
              type="text"
              value={formData.postalCode}
              onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
              placeholder="12345"
              className="w-full px-4 py-3 rounded-xl text-sm"
              style={{ 
                backgroundColor: COLORS.bgCard,
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textPrimary,
              }}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block" style={{ color: COLORS.textSecondary }}>
              Stadt
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
              placeholder="Köln"
              className="w-full px-4 py-3 rounded-xl text-sm"
              style={{ 
                backgroundColor: COLORS.bgCard,
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textPrimary,
              }}
            />
          </div>
        </div>
      </div>

      {/* Continue Button */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={() => setStep(3)}
        className="w-full mt-6 py-4 rounded-xl font-bold flex items-center justify-center gap-2"
        style={{ backgroundColor: COLORS.primary, color: COLORS.white }}
      >
        Weiter zur Zahlung
        <ArrowRight className="w-5 h-5" />
      </motion.button>
    </motion.div>
  );

  // ═══════════════════════════════════════════════════════════
  // STEP 3: PAYMENT
  // ═══════════════════════════════════════════════════════════
  const PaymentStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="px-5 py-6"
    >
      <h2 className="text-lg font-bold mb-1" style={{ color: COLORS.textPrimary }}>
        Zahlung & Bestätigung
      </h2>
      <p className="text-sm mb-6" style={{ color: COLORS.textMuted }}>
        Wähle deine Zahlungsmethode und bestätige dein Investment.
      </p>

      {/* Payment Method */}
      <div className="mb-6">
        <label className="text-sm font-medium mb-3 block" style={{ color: COLORS.textSecondary }}>
          Zahlungsmethode
        </label>
        
        <div className="space-y-2">
          {/* Bank Transfer */}
          <div 
            className="p-4 rounded-xl flex items-center gap-3 cursor-pointer"
            style={{ 
              backgroundColor: COLORS.bgCard,
              border: `2px solid ${COLORS.primary}`,
            }}
          >
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${COLORS.primary}20` }}
            >
              <Building className="w-5 h-5" style={{ color: COLORS.primary }} />
            </div>
            <div className="flex-1">
              <p className="font-medium" style={{ color: COLORS.textPrimary }}>Banküberweisung</p>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>SEPA Überweisung</p>
            </div>
            <div 
              className="w-5 h-5 rounded-full flex items-center justify-center"
              style={{ backgroundColor: COLORS.primary }}
            >
              <Check className="w-3 h-3 text-white" />
            </div>
          </div>

          {/* Credit Card - Disabled */}
          <div 
            className="p-4 rounded-xl flex items-center gap-3 opacity-50"
            style={{ 
              backgroundColor: COLORS.bgCard,
              border: `1px solid ${COLORS.border}`,
            }}
          >
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: COLORS.bgCardLight }}
            >
              <CreditCard className="w-5 h-5" style={{ color: COLORS.textMuted }} />
            </div>
            <div className="flex-1">
              <p className="font-medium" style={{ color: COLORS.textPrimary }}>Kreditkarte</p>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>Demnächst verfügbar</p>
            </div>
          </div>
        </div>
      </div>

      {/* IBAN Input */}
      <div className="mb-6">
        <label className="text-sm font-medium mb-2 block" style={{ color: COLORS.textSecondary }}>
          IBAN (für Auszahlungen)
        </label>
        <input
          type="text"
          value={formData.iban}
          onChange={(e) => setFormData({...formData, iban: e.target.value})}
          placeholder="DE89 3704 0044 0532 0130 00"
          className="w-full px-4 py-3 rounded-xl text-sm font-mono"
          style={{ 
            backgroundColor: COLORS.bgCard,
            border: `1px solid ${COLORS.border}`,
            color: COLORS.textPrimary,
          }}
        />
        <p className="text-xs mt-1" style={{ color: COLORS.textMuted }}>
          Auf dieses Konto werden deine Gewinnausschüttungen überwiesen.
        </p>
      </div>

      {/* Legal Agreements */}
      <div className="mb-6 space-y-3">
        <label className="text-sm font-medium mb-3 block" style={{ color: COLORS.textSecondary }}>
          Rechtliche Bestätigungen
        </label>

        {/* Risk Acknowledgement */}
        <label 
          className="flex items-start gap-3 p-4 rounded-xl cursor-pointer"
          style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
        >
          <input
            type="checkbox"
            checked={acceptedTerms.risk}
            onChange={(e) => setAcceptedTerms({...acceptedTerms, risk: e.target.checked})}
            className="mt-1 w-5 h-5 rounded"
            style={{ accentColor: COLORS.primary }}
          />
          <span className="text-sm" style={{ color: COLORS.textSecondary }}>
            Ich verstehe, dass dies eine unternehmerische Beteiligung mit Risiko ist und ein 
            vollständiger Verlust meines Kapitals möglich ist.
          </span>
        </label>

        {/* Terms */}
        <label 
          className="flex items-start gap-3 p-4 rounded-xl cursor-pointer"
          style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
        >
          <input
            type="checkbox"
            checked={acceptedTerms.terms}
            onChange={(e) => setAcceptedTerms({...acceptedTerms, terms: e.target.checked})}
            className="mt-1 w-5 h-5 rounded"
            style={{ accentColor: COLORS.primary }}
          />
          <span className="text-sm" style={{ color: COLORS.textSecondary }}>
            Ich akzeptiere die <span style={{ color: COLORS.primary }}>AGB</span> und 
            den <span style={{ color: COLORS.primary }}>Beteiligungsvertrag</span>.
          </span>
        </label>

        {/* Privacy */}
        <label 
          className="flex items-start gap-3 p-4 rounded-xl cursor-pointer"
          style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
        >
          <input
            type="checkbox"
            checked={acceptedTerms.privacy}
            onChange={(e) => setAcceptedTerms({...acceptedTerms, privacy: e.target.checked})}
            className="mt-1 w-5 h-5 rounded"
            style={{ accentColor: COLORS.primary }}
          />
          <span className="text-sm" style={{ color: COLORS.textSecondary }}>
            Ich stimme der <span style={{ color: COLORS.primary }}>Datenschutzerklärung</span> zu.
          </span>
        </label>
      </div>

      {/* Summary */}
      <div 
        className="p-4 rounded-xl mb-6"
        style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center justify-between mb-2">
          <span style={{ color: COLORS.textMuted }}>Investment</span>
          <span style={{ color: COLORS.textPrimary }}>{level.name}</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span style={{ color: COLORS.textMuted }}>Standort</span>
          <span style={{ color: COLORS.textPrimary }}>{city.name}</span>
        </div>
        <div className="flex items-center justify-between pt-2" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <span className="font-bold" style={{ color: COLORS.textPrimary }}>Gesamtbetrag</span>
          <span className="text-xl font-bold" style={{ color: COLORS.primary }}>
            {level.amount.toLocaleString('de-DE')}€
          </span>
        </div>
      </div>

      {/* Submit Button */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={handleSubmit}
        disabled={!allTermsAccepted || isProcessing}
        className="w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2"
        style={{ 
          backgroundColor: allTermsAccepted ? COLORS.primary : COLORS.bgCardLight,
          color: allTermsAccepted ? COLORS.white : COLORS.textMuted,
        }}
      >
        {isProcessing ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Wird verarbeitet...
          </>
        ) : (
          <>
            Investment bestätigen
            <Shield className="w-5 h-5" />
          </>
        )}
      </motion.button>

      <p className="text-center text-xs mt-4" style={{ color: COLORS.textMuted }}>
        <Shield className="w-3 h-3 inline mr-1" />
        Sichere Verbindung · Daten werden verschlüsselt übertragen
      </p>
    </motion.div>
  );

  // ═══════════════════════════════════════════════════════════
  // STEP 4: SUCCESS
  // ═══════════════════════════════════════════════════════════
  const SuccessStep = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="px-5 py-8 text-center"
    >
      {/* Success Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring" }}
        className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
        style={{ backgroundColor: `${COLORS.primary}20` }}
      >
        <CheckCircle2 className="w-10 h-10" style={{ color: COLORS.primary }} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h2 className="text-2xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
          Willkommen im Team!
        </h2>
        <p className="text-sm mb-6" style={{ color: COLORS.textSecondary }}>
          Du bist jetzt offiziell {level.name} bei Franky's {city.name}.
        </p>
      </motion.div>

      {/* Investment Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="p-5 rounded-2xl mb-6 text-left"
        style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${level.color}40` }}
      >
        <div className="flex items-center gap-4 mb-4">
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl"
            style={{ backgroundColor: `${level.color}20` }}
          >
            {level.emoji}
          </div>
          <div>
            <p className="font-bold" style={{ color: COLORS.textPrimary }}>{level.name}</p>
            <p className="text-sm" style={{ color: COLORS.textMuted }}>{city.name}</p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-xl font-bold" style={{ color: level.color }}>
              {level.amount.toLocaleString('de-DE')}€
            </p>
          </div>
        </div>

        <p className="text-sm mb-4" style={{ color: COLORS.textSecondary }}>
          Deine nächsten Schritte:
        </p>

        <div className="space-y-3">
          {[
            "Überweise den Betrag auf das angegebene Konto",
            "Bestätigungs-E-Mail prüfen",
            "Beteiligungsvertrag unterschreiben",
            "Im Investor Dashboard Updates verfolgen",
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: COLORS.primary, color: COLORS.white }}
              >
                {i + 1}
              </div>
              <span className="text-sm" style={{ color: COLORS.textPrimary }}>{item}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Bank Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="p-4 rounded-xl mb-6 text-left"
        style={{ backgroundColor: `${COLORS.gold}10`, border: `1px solid ${COLORS.gold}30` }}
      >
        <p className="font-semibold text-sm mb-2" style={{ color: COLORS.gold }}>
          Überweisungsdaten
        </p>
        <div className="space-y-1 text-sm">
          <p style={{ color: COLORS.textSecondary }}>
            <span style={{ color: COLORS.textMuted }}>Empfänger:</span> Franky's Farm GmbH
          </p>
          <p style={{ color: COLORS.textSecondary }}>
            <span style={{ color: COLORS.textMuted }}>IBAN:</span> DE89 3704 0044 0532 0130 00
          </p>
          <p style={{ color: COLORS.textSecondary }}>
            <span style={{ color: COLORS.textMuted }}>Verwendungszweck:</span> INV-{cityId?.toUpperCase()}-{Date.now().toString(36).toUpperCase()}
          </p>
        </div>
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="space-y-3"
      >
        <button
          onClick={() => navigate("/invest/dashboard")}
          className="w-full py-4 rounded-xl font-bold"
          style={{ backgroundColor: COLORS.primary, color: COLORS.white }}
        >
          Zum Investor Dashboard
        </button>
        
        <button
          onClick={() => navigate("/landing")}
          className="w-full py-3 rounded-xl font-medium"
          style={{ color: COLORS.textSecondary }}
        >
          Zurück zur App
        </button>
      </motion.div>
    </motion.div>
  );

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: COLORS.bgDark }}
    >
      {/* Header */}
      <header className="px-4 pt-6 pb-4 flex items-center gap-3">
        <motion.button 
          whileTap={{ scale: 0.9 }} 
          onClick={() => step > 1 && step < 4 ? setStep(step - 1) : navigate(`/invest/city/${cityId}`)}
          className="p-2 rounded-xl"
          style={{ backgroundColor: COLORS.bgCardLight }}
        >
          <ChevronLeft className="w-5 h-5" style={{ color: COLORS.textPrimary }} />
        </motion.button>
        <div className="flex-1">
          <h1 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
            {step === 4 ? "Erfolgreich!" : "Investment abschließen"}
          </h1>
          {step < 4 && (
            <p className="text-xs" style={{ color: COLORS.textMuted }}>
              Schritt {step} von 3
            </p>
          )}
        </div>
      </header>

      {/* Progress Indicator */}
      {step < 4 && (
        <div className="px-5 pb-4">
          <div className="flex gap-2">
            {[1, 2, 3].map((s) => (
              <div 
                key={s}
                className="flex-1 h-1 rounded-full"
                style={{ 
                  backgroundColor: s <= step ? COLORS.primary : COLORS.bgCardLight 
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <AnimatePresence mode="wait">
        {renderStep()}
      </AnimatePresence>
    </div>
  );
}
